import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCalculatorStore } from '../../store/calculatorStore';
import { formatCurrency } from '../../utils/format';
import { format, getYear } from 'date-fns';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import type { AmortizationEntry } from '../../store/calculatorStore';
import debounce from 'lodash/debounce';
import { scrollElementTopBelowAppChrome } from '../../utils/scrollBelowAppChrome';
import { TOUR_AMORTIZATION_TABLE_SELECTOR, TOUR_EXPAND_AMORT_FIRST_YEAR_EVENT } from '../tutorial/TutorialDriver';

const STICKY_TOP_FALLBACK = 114;

const COLUMN_HEADERS = [
  'Payment Date',
  'Total',
  'Principal',
  'Interest',
  'Expenses',
  'Extra',
  'Balance',
];

interface AmortizationTableProps {
  tabId: string;
}

interface YearlySummary {
  year: number;
  totalPrincipal: number;
  totalInterest: number;
  totalExpenses: number;
  totalExtraPayments: number;
  totalPmi: number;
  remainingBalance: number;
  entries: { entry: AmortizationEntry; scheduleIndex: number }[];
}

export const AmortizationTable: React.FC<AmortizationTableProps> = ({ tabId }) => {
  const calculatorTabs = useCalculatorStore(s => s.calculatorTabs);
  const updateCalculatorTabData = useCalculatorStore(s => s.updateCalculatorTabData);
  const calculateAmortizationSchedule = useCalculatorStore(s => s.calculateAmortizationSchedule);
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const [localExtraPayments, setLocalExtraPayments] = useState<{ [key: number]: string }>({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{ [key: number]: string }>({});
  const [manualExtraPayments, setManualExtraPayments] = useState<{ [key: number]: number }>({});
  const isUpdatingRef = useRef(false);
  const isClearingManualPaymentsRef = useRef(false);
  const calculationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const previousManualEditsRef = useRef<boolean>();

  // Sticky header refs & state
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const tableBottomSentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [stickyLeft, setStickyLeft] = useState(0);
  const [stickyWidth, setStickyWidth] = useState(0);
  const [colWidths, setColWidths] = useState<number[]>([]);
  const [headerScrollLeft, setHeaderScrollLeft] = useState(0);
  const [stickyTop, setStickyTop] = useState(STICKY_TOP_FALLBACK);

  // Scroll overflow indicator
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Dynamically measure the combined height of the sticky nav + tab bar
  useEffect(() => {
    const measure = () => {
      const nav = document.getElementById('app-nav');
      const tabBar = document.getElementById('calculator-tab-bar');
      setStickyTop((nav?.offsetHeight ?? 64) + (tabBar?.offsetHeight ?? 50));
    };
    measure();
    const ro = new ResizeObserver(measure);
    const nav = document.getElementById('app-nav');
    const tabBar = document.getElementById('calculator-tab-bar');
    if (nav) ro.observe(nav);
    if (tabBar) ro.observe(tabBar);
    return () => ro.disconnect();
  }, []);
  
  const tab = calculatorTabs.find(t => t.id === tabId);
  const amortizationSchedule = tab?.data?.amortizationSchedule;
  const mortgageTerms = tab?.data?.mortgageTerms;
  const country = mortgageTerms?.country || 'USA';
  
  // Group entries by year and calculate yearly summaries
  const yearlySummaries = useMemo(() => {
    if (!amortizationSchedule || amortizationSchedule.length === 0) {
      return [];
    }

    const summaries: YearlySummary[] = [];
    let currentYear = -1;
    let currentSummary: YearlySummary | null = null;

    amortizationSchedule.forEach((entry, index) => {
      const year = getYear(entry.date);
      
      if (year !== currentYear) {
        if (currentSummary) {
          summaries.push(currentSummary);
        }
        currentYear = year;
        currentSummary = {
          year,
          totalPrincipal: 0,
          totalInterest: 0,
          totalExpenses: 0,
          totalExtraPayments: 0,
          totalPmi: 0,
          remainingBalance: 0,
          entries: []
        };
      }

      if (currentSummary) {
        currentSummary.totalPrincipal += entry.principalPayment;
        currentSummary.totalInterest += entry.interestPayment;
        currentSummary.totalExpenses += entry.expensePayment;
        currentSummary.totalExtraPayments += (entry.oneTimePayment || 0);
        currentSummary.totalPmi += (entry.pmiPayment || 0);
        currentSummary.remainingBalance = entry.remainingBalance;
        currentSummary.entries.push({ entry, scheduleIndex: index });
      }
    });

    if (currentSummary) {
      summaries.push(currentSummary);
    }

    return summaries;
  }, [amortizationSchedule]);

  const yearlySummariesRef = useRef(yearlySummaries);
  yearlySummariesRef.current = yearlySummaries;

  useEffect(() => {
    const handler = () => {
      const firstYear = yearlySummariesRef.current[0]?.year;
      if (firstYear === undefined) return;
      setExpandedYears(prev => {
        if (prev.has(firstYear)) return prev;
        const next = new Set(prev);
        next.add(firstYear);
        return next;
      });
      window.setTimeout(() => {
        scrollElementTopBelowAppChrome(TOUR_AMORTIZATION_TABLE_SELECTOR);

        const root = scrollContainerRef.current;
        if (!root) return;
        const input = root.querySelector<HTMLElement>('input[aria-label^="One-time extra"]');
        if (!input) return;

        const pad = 8;
        const c = root.getBoundingClientRect();
        const i = input.getBoundingClientRect();
        if (i.right > c.right - pad) {
          root.scrollLeft += i.right - c.right + pad;
        } else if (i.left < c.left + pad) {
          root.scrollLeft -= c.left + pad - i.left;
        }
      }, 0);
    };
    window.addEventListener(TOUR_EXPAND_AMORT_FIRST_YEAR_EVENT, handler);
    return () => window.removeEventListener(TOUR_EXPAND_AMORT_FIRST_YEAR_EVENT, handler);
  }, []);

  // Effect to handle manual edits flag changes
  useEffect(() => {
    if (!tab?.data?.hasManualExtraPaymentEdits && previousManualEditsRef.current) {
      isClearingManualPaymentsRef.current = true;
      setManualExtraPayments({});
      setLocalExtraPayments({});
      setPendingChanges({});
      setTimeout(() => {
        isClearingManualPaymentsRef.current = false;
      }, 0);
    }
    previousManualEditsRef.current = tab?.data?.hasManualExtraPaymentEdits;
  }, [tab?.data?.hasManualExtraPaymentEdits]);

  // Effect to handle schedule updates
  useEffect(() => {
    if (amortizationSchedule && !isUpdatingRef.current && !isClearingManualPaymentsRef.current) {
      // Only update local state if we don't have pending changes
      if (Object.keys(pendingChanges).length === 0) {
        const newLocalState: { [key: number]: string } = {};
        amortizationSchedule.forEach((entry, index) => {
          if (entry.oneTimePayment !== undefined) {
            newLocalState[index] = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 2
            }).format(entry.oneTimePayment);
          }
        });
        setLocalExtraPayments(newLocalState);
      }
    }
  }, [amortizationSchedule, pendingChanges, tab?.data?.hasManualExtraPaymentEdits]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
    };
  }, []);

  // Measure the scroll container bounds and each column width
  const measureLayout = useCallback(() => {
    if (scrollContainerRef.current) {
      const rect = scrollContainerRef.current.getBoundingClientRect();
      setStickyLeft(rect.left);
      setStickyWidth(rect.width);
    }
    if (theadRef.current) {
      const ths = Array.from(theadRef.current.querySelectorAll('th'));
      setColWidths(ths.map(th => th.getBoundingClientRect().width));
    }
  }, []);

  // Show/hide sticky header when thead scrolls past the sticky zone; hide once the user
  // scrolls past the bottom of the table so content below (e.g. province guides) is unobstructed.
  useEffect(() => {
    const thead = theadRef.current;
    const bottomSentinel = tableBottomSentinelRef.current;
    if (!thead || !bottomSentinel) return;

    let theadScrolledPast = false;
    let bottomScrolledPast = false;

    const updateStickyHeader = () => {
      const shouldShow = theadScrolledPast && !bottomScrolledPast;
      setShowStickyHeader(shouldShow);
      if (shouldShow) measureLayout();
    };

    const observerOptions: IntersectionObserverInit = {
      rootMargin: `-${stickyTop}px 0px 0px 0px`,
      threshold: 0,
    };

    const onIntersect =
      (which: 'thead' | 'bottom') =>
      ([entry]: IntersectionObserverEntry[]) => {
        const isAboveStickyZone = entry.boundingClientRect.top < stickyTop;
        const scrolledPast = !entry.isIntersecting && isAboveStickyZone;
        if (which === 'thead') {
          theadScrolledPast = scrolledPast;
        } else {
          bottomScrolledPast = scrolledPast;
        }
        updateStickyHeader();
      };

    const theadObserver = new IntersectionObserver(onIntersect('thead'), observerOptions);
    const bottomObserver = new IntersectionObserver(onIntersect('bottom'), observerOptions);

    theadObserver.observe(thead);
    bottomObserver.observe(bottomSentinel);

    return () => {
      setShowStickyHeader(false);
      theadObserver.disconnect();
      bottomObserver.disconnect();
    };
  }, [measureLayout, stickyTop, tabId]);

  // Sync horizontal scroll of the fixed header with the real table container
  // and track whether the user can scroll further right (for the fade indicator)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateScrollState = () => {
      setHeaderScrollLeft(container.scrollLeft);
      setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth - 1);
    };

    container.addEventListener('scroll', updateScrollState, { passive: true });

    const ro = new ResizeObserver(updateScrollState);
    ro.observe(container);
    // also observe the inner table so we detect when its width changes
    const table = container.querySelector('table');
    if (table) ro.observe(table);

    // initial check
    updateScrollState();

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, []);

  // Re-measure on window resize so the fixed header stays aligned
  useEffect(() => {
    if (!showStickyHeader) return;
    const handleResize = () => measureLayout();
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [showStickyHeader, measureLayout]);

  // Debounced calculation function
  const debouncedCalculate = useMemo(
    () =>
    debounce((schedule: AmortizationEntry[]) => {
      // Show loading state only after debounce period
      setIsCalculating(true);
      isUpdatingRef.current = true;
      updateCalculatorTabData(tabId, {
        amortizationSchedule: schedule,
        hasManualExtraPaymentEdits: true
      });
      calculateAmortizationSchedule(tabId);
      setPendingChanges({});
    }, 1500),
    [tabId, updateCalculatorTabData, calculateAmortizationSchedule]
  );

  useEffect(() => {
    return () => debouncedCalculate.cancel();
  }, [debouncedCalculate]);

  // Handle store updates
  useEffect(() => {
    if (tab?.data?.amortizationSchedule) {
      // Only update if we're not in the middle of an update
      if (!isUpdatingRef.current) {
        // If we have manual payments but the store doesn't have manual edits flag,
        // AND we're not in the process of clearing manual payments,
        // update the store to preserve our manual payments
        if (Object.keys(manualExtraPayments).length > 0 && 
            !tab.data.hasManualExtraPaymentEdits && 
            !isClearingManualPaymentsRef.current) {
          isUpdatingRef.current = true;
          updateCalculatorTabData(tabId, {
            hasManualExtraPaymentEdits: true
          });
        }
      } else {
        isUpdatingRef.current = false;
      }

      // Clear the calculation timeout
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
      calculationTimeoutRef.current = setTimeout(() => {
        setIsCalculating(false);
        // Clear pending changes after calculation is complete
        setPendingChanges({});
      }, 1000); // Show loading mask for 1 second after calculation completes
    }
  }, [
    manualExtraPayments,
    tab?.data?.amortizationSchedule,
    tab?.data?.hasManualExtraPaymentEdits,
    tabId,
    updateCalculatorTabData,
  ]);

  const toggleYear = (year: number) => {
    setExpandedYears(prev => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  };

  const handleExtraPaymentChange = (index: number, value: string) => {
    if (!amortizationSchedule || !mortgageTerms) return;
    const entry = amortizationSchedule[index];
    if (!entry) return;

    // Remove dollar sign and commas for calculation
    const cleanValue = value.replace(/[$,]/g, '');

    // Handle empty string case
    if (cleanValue === '') {
      value = '0';
    }

    const numValue = parseFloat(cleanValue);
    if (isNaN(numValue) || numValue < 0) return;

    // Don't allow extra payment larger than remaining balance
    if (numValue > entry.remainingBalance) return;

    // Format the value with commas and dollar sign for display
    const formattedValue = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(numValue);

    // Update local state immediately for responsive UI
    setLocalExtraPayments(prev => ({
      ...prev,
      [index]: formattedValue
    }));

    // Track pending changes
    setPendingChanges(prev => ({
      ...prev,
      [index]: formattedValue
    }));

    // Store the manual extra payment
    setManualExtraPayments(prev => {
      const newPayments = {
        ...prev,
        [index]: numValue
      };
      return newPayments;
    });

    // Only trigger calculation if the value actually changed
    const currentValue = entry.oneTimePayment?.toString() || '0';
    if (cleanValue !== currentValue) {
      // Create a new schedule with the updated payment
      const updatedSchedule = [...amortizationSchedule];
      updatedSchedule[index] = {
        ...entry,
        oneTimePayment: numValue,
        totalPayment: entry.payment + entry.expensePayment + numValue
      };

      // Trigger the shared engine-side cascade so PMI and insurance remain consistent.
      debouncedCalculate(updatedSchedule);
    }
  };

  if (!tab) {
    return <div className="p-4 text-center text-ink-muted">Calculator not found</div>;
  }
  
  if (!amortizationSchedule || amortizationSchedule.length === 0) {
    return <div className="p-4 text-center text-ink-muted">Complete the required fields to see the amortization schedule</div>;
  }
  
  // Schedule is always one row per month (any sub-monthly cadence is aggregated), so use monthly date format
  const dateFormat = 'MMM yyyy';

  const thClass = 'px-3 py-2 text-left text-[10px] font-semibold text-ink-muted uppercase tracking-widest';
  const tdClass = 'px-3 py-2 whitespace-nowrap text-xs text-ink font-mono';

  return (
    <div className="pb-5 min-w-0">
      {isCalculating && (
        <div
          className="fixed inset-0 bg-navy-900/60 flex items-center justify-center z-50"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="bg-white p-5 rounded-2xl shadow-panel">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500 mx-auto" />
            <p className="mt-3 text-sm text-ink-muted">Updating calculations...</p>
          </div>
        </div>
      )}
      <div className="relative">
        {canScrollRight && (
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-12 z-10"
            style={{ background: 'linear-gradient(to right, transparent, white)' }}
          />
        )}
      <div className="overflow-x-auto w-full" ref={scrollContainerRef}>
        <table className="w-full min-w-max divide-y divide-stone-border">
          <thead className="bg-stone" ref={theadRef}>
            <tr>
              {COLUMN_HEADERS.map(h => (
                <th key={h} scope="col" className={thClass}>
                  {h.split(' ').map((word, i, arr) => (
                    <React.Fragment key={i}>
                      {word}{i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-stone-border">
            {yearlySummaries.map((summary) => (
              <React.Fragment key={summary.year}>
                {/* Yearly Summary Row */}
                <tr
                  className="bg-navy-50 cursor-pointer hover:bg-navy-100 transition-colors"
                  onClick={() => toggleYear(summary.year)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleYear(summary.year);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={expandedYears.has(summary.year)}
                  aria-label={`${expandedYears.has(summary.year) ? 'Collapse' : 'Expand'} ${summary.year} payment details`}
                >
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-navy-800 font-semibold">
                    <div className="flex items-center gap-2">
                      <ChevronRightIcon
                        className={`h-4 w-4 transition-transform text-gold-600 ${expandedYears.has(summary.year) ? 'rotate-90' : ''}`}
                      />
                      {summary.year}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-navy-800 font-mono">
                    {formatCurrency(summary.totalPrincipal + summary.totalInterest + summary.totalPmi + summary.totalExpenses + summary.totalExtraPayments, country)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-navy-800 font-mono">{formatCurrency(summary.totalPrincipal, country)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-navy-800 font-mono">{formatCurrency(summary.totalInterest, country)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-navy-800 font-mono">{formatCurrency(summary.totalExpenses + summary.totalPmi, country)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-navy-800 font-mono">{formatCurrency(summary.totalExtraPayments, country)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-navy-800 font-mono">{formatCurrency(summary.remainingBalance, country)}</td>
                </tr>

                {/* Detailed Payment Rows */}
                {expandedYears.has(summary.year) && summary.entries.map(({ entry, scheduleIndex }, index) => {
                  const termDetails = mortgageTerms?.canadianTermDetails;
                  const contractRate = mortgageTerms?.rate;
                  const fromRate =
                    scheduleIndex > 0
                      ? amortizationSchedule[scheduleIndex - 1]?.currentRate ?? contractRate
                      : contractRate;
                  const toRate = entry.currentRate ?? contractRate;
                  const showRenewalRateCopy =
                    termDetails?.showRenewalScenario &&
                    fromRate !== undefined &&
                    toRate !== undefined &&
                    fromRate !== toRate;
                  const showUnchangedRenewalRate =
                    termDetails?.showRenewalScenario &&
                    fromRate !== undefined &&
                    toRate !== undefined &&
                    fromRate === toRate;

                  return (
                    <React.Fragment key={`${summary.year}-${index}`}>
                      {entry.isRenewalDate && (
                        <tr>
                          <td colSpan={7} className="px-3 py-2 bg-gold-50 border-t-2 border-b-2 border-gold-400">
                            <div className="flex items-center gap-2 text-gold-700 text-xs font-semibold">
                              <span>🔄 Term Renewal</span>
                              {showRenewalRateCopy ? (
                                <span className="font-normal">
                                  - rate changes from <strong>{fromRate}%</strong> to <strong>{toRate}%</strong>
                                  {toRate > fromRate ? (
                                    <span className="ml-1 text-error-600">(↑ higher payments)</span>
                                  ) : toRate < fromRate ? (
                                    <span className="ml-1 text-live">(↓ lower payments)</span>
                                  ) : null}
                                </span>
                              ) : showUnchangedRenewalRate ? (
                                <span className="font-normal">
                                  - rate unchanged at <strong>{toRate}%</strong>
                                </span>
                              ) : (
                                <span className="font-normal text-gold-600">- new rate not yet modeled</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      <tr className={index % 2 === 0 ? 'bg-white hover:bg-cream' : 'bg-stone/30 hover:bg-cream'}>
                        <td className={`${tdClass} text-ink-muted pl-8`}>{format(entry.date, dateFormat)}</td>
                        <td className={tdClass}>{formatCurrency(entry.totalPayment, country)}</td>
                        <td className={tdClass}>{formatCurrency(entry.principalPayment, country)}</td>
                        <td className={tdClass}>{formatCurrency(entry.interestPayment, country)}</td>
                        <td className={tdClass}>{formatCurrency(entry.expensePayment + (entry.pmiPayment ?? 0), country)}</td>
                        <td className={tdClass}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={localExtraPayments[scheduleIndex] || ''}
                            aria-label={`One-time extra payment for ${format(entry.date, dateFormat)}`}
                            onChange={e => handleExtraPaymentChange(scheduleIndex, e.target.value)}
                            onBlur={e => {
                              if (pendingChanges[scheduleIndex]) handleExtraPaymentChange(scheduleIndex, e.target.value);
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleExtraPaymentChange(scheduleIndex, e.currentTarget.value);
                            }}
                            className="w-14 min-w-14 focus:w-28 focus:min-w-28 transition-[width] duration-200 ease-out px-2 py-1 border border-stone-border rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-gold-400 bg-white"
                            min="0"
                            max={entry.remainingBalance}
                            step="0.01"
                            placeholder="$0"
                          />
                        </td>
                        <td className={tdClass}>{formatCurrency(entry.remainingBalance, country)}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div ref={tableBottomSentinelRef} className="h-px w-full" aria-hidden="true" />
      </div>

      {showStickyHeader && createPortal(
        <div
          className="bg-stone shadow-md border-b-2 border-stone-border"
          style={{ position: 'fixed', top: stickyTop, left: stickyLeft, width: stickyWidth, zIndex: 30, overflow: 'hidden' }}
        >
          <table
            className="min-w-full"
            style={{ tableLayout: colWidths.length ? 'fixed' : 'auto', transform: `translateX(${-headerScrollLeft}px)` }}
          >
            {colWidths.length > 0 && (
              <colgroup>
                {colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
              </colgroup>
            )}
            <thead>
              <tr>
                {COLUMN_HEADERS.map(header => (
                  <th key={header} scope="col" className={thClass}>
                    {header.split(' ').map((word, i, arr) => (
                      <React.Fragment key={i}>
                        {word}{i < arr.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>,
        document.body
      )}
    </div>
  );
};